import {
  StudentContestCertificateType,
  StudentContestResultData,
} from 'APIs/letrusAPI/studentContestResult/types';
import {certificateAssets} from './certificateAssets';

export interface CertificateSegment {
  text: string;
  bold?: boolean;
}

export interface CertificateLine {
  segments: CertificateSegment[];
}

export interface ContestCertificateViewModel {
  title: string;
  subtitle?: string;
  lines: CertificateLine[];
  backgroundImage: string;
  medalImage?: string;
  downloadFileName: string;
}

const EVENT_DATE_RANGE = '17 e 28 de agosto de 2026';

function createDownloadFileName(certificateType: string) {
  return `certificado-olimpiada-${certificateType}`;
}

function getSchoolGradeLabel(result: StudentContestResultData) {
  return result.school_grade;
}

function buildParticipationViewModel(
  result: StudentContestResultData,
  participationVerb: 'concluiu sua participação' | 'participou da',
): ContestCertificateViewModel {
  return {
    title: 'Certificado de Participação',
    backgroundImage: certificateAssets.backgrounds.participation,
    downloadFileName: createDownloadFileName('participacao'),
    lines: [
      {
        segments: [
          {text: 'A Letrus', bold: true},
          {text: ' certifica que '},
          {text: result.student_name, bold: true},
          {text: ', estudante da série '},
          {text: getSchoolGradeLabel(result), bold: true},
          {text: ' na escola '},
          {text: result.school_name, bold: true},
          {text: ` ${participationVerb} na `},
          {
            text: 'Linguagem em Ação - Olimpíada Nacional de Redação da Letrus',
            bold: true,
          },
          {text: `, realizada entre os dias ${EVENT_DATE_RANGE}.`},
        ],
      },
    ],
  };
}

function buildFeaturedCompositionViewModel(
  result: StudentContestResultData,
): ContestCertificateViewModel {
  return {
    title: 'Certificado de Redação Destaque',
    backgroundImage: certificateAssets.backgrounds.featuredComposition,
    downloadFileName: createDownloadFileName('redacao-destaque'),
    lines: [
      {
        segments: [
          {text: 'A Letrus', bold: true},
          {text: ' certifica que '},
          {text: result.student_name, bold: true},
          {text: ', estudante da série '},
          {text: getSchoolGradeLabel(result), bold: true},
          {text: ' na escola '},
          {text: result.school_name, bold: true},
          {text: ' teve sua redação reconhecida como '},
          {text: 'Redação Destaque', bold: true},
          {text: ' do ciclo '},
          {text: result.cycle_name, bold: true},
          {text: ' e conquistou '},
          {text: 'Medalha de Ouro', bold: true},
          {text: ' na '},
          {
            text: 'Linguagem em Ação - Olimpíada Nacional de Redação da Letrus',
            bold: true,
          },
          {text: `, realizada entre os dias ${EVENT_DATE_RANGE}.`},
        ],
      },
    ],
  };
}

function buildMedalViewModel(
  result: StudentContestResultData,
  certificateType: Extract<
    StudentContestCertificateType,
    'gold' | 'silver' | 'bronze'
  >,
): ContestCertificateViewModel {
  const medalNameMap = {
    gold: 'Ouro',
    silver: 'Prata',
    bronze: 'Bronze',
  } as const;

  const medalName = medalNameMap[certificateType];

  return {
    title: 'Certificado de Reconhecimento',
    subtitle: `MEDALHISTA DE ${medalName.toUpperCase()}`,
    backgroundImage: certificateAssets.backgrounds.medal,
    medalImage: certificateAssets.medals[certificateType],
    downloadFileName: createDownloadFileName(`medalha-${certificateType}`),
    lines: [
      {
        segments: [
          {text: 'A Letrus certifica que '},
          {text: result.student_name, bold: true},
          {text: ', estudante do(a) '},
          {text: getSchoolGradeLabel(result), bold: true},
          {text: ' da '},
          {text: result.school_name, bold: true},
          {text: ', conquistou a '},
          {text: `Medalha de ${medalName}`, bold: true},
          {text: ' na '},
          {
            text: 'Linguagem em Ação — Olimpíada Nacional de Redação da Letrus',
            bold: true,
          },
          {
            text: ', em reconhecimento ao seu desempenho no ranking de notas do ',
          },
          {text: result.cycle_name, bold: true},
          {text: `, realizada entre os dias ${EVENT_DATE_RANGE}.`},
        ],
      },
    ],
  };
}

export function getContestCertificateViewModel(
  result: StudentContestResultData | null,
): ContestCertificateViewModel | null {
  if (!result) {
    return null;
  }

  if (result.final_score == null) {
    return buildParticipationViewModel(result, 'participou da');
  }

  switch (result.certificate_type) {
    case 'participation':
      return buildParticipationViewModel(result, 'concluiu sua participação');
    case 'featured_composition':
      return buildFeaturedCompositionViewModel(result);
    case 'gold':
    case 'silver':
    case 'bronze':
      return buildMedalViewModel(result, result.certificate_type);
    default:
      return null;
  }
}
